import type { RouteLocationNormalized } from 'vue-router';

const DEFAULT_RECORDER_SAMPLE_RATE = '0.15';
const DEFAULT_RECORDER_MASK_LEVEL = 'moderate';
const DEFAULT_RECORDER_MAX_DURATION = '300000';

let initialized = false;

declare global {
  interface Window {
    umami?: {
      track: (
        event?: string | ((props: Record<string, unknown>) => Record<string, unknown>),
        data?: Record<string, unknown>,
      ) => void;
    };
  }
}

function readEnv(name: keyof ImportMetaEnv): string | null {
  const value = import.meta.env[name];
  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim();
  }
  return null;
}

function resolveWebsiteId(): string | null {
  return readEnv('VITE_UMAMI_WEBSITE_ID');
}

function resolveScriptUrl(): string | null {
  return readEnv('VITE_UMAMI_SCRIPT_URL');
}

function resolveRecorderUrl(scriptUrl: string): string | null {
  const explicit = readEnv('VITE_UMAMI_RECORDER_URL');
  if (explicit) {
    return explicit;
  }

  if (readEnv('VITE_UMAMI_RECORDER_ENABLED') === 'false') {
    return null;
  }

  if (!scriptUrl.endsWith('/script.js')) {
    return null;
  }

  return scriptUrl.replace(/\/script\.js$/, '/recorder.js');
}

function appendScript(
  src: string,
  dataset: Record<string, string>,
): void {
  const script = document.createElement('script');
  script.defer = true;
  script.src = src;

  for (const [key, value] of Object.entries(dataset)) {
    script.dataset[key] = value;
  }

  document.head.appendChild(script);
}

export function initUmami(): void {
  const websiteId = resolveWebsiteId();
  const scriptUrl = resolveScriptUrl();
  if (!websiteId || !scriptUrl) {
    return;
  }

  appendScript(scriptUrl, { websiteId });

  const recorderUrl = resolveRecorderUrl(scriptUrl);
  if (recorderUrl) {
    appendScript(recorderUrl, {
      websiteId,
      sampleRate: readEnv('VITE_UMAMI_RECORDER_SAMPLE_RATE') ?? DEFAULT_RECORDER_SAMPLE_RATE,
      maskLevel: readEnv('VITE_UMAMI_RECORDER_MASK_LEVEL') ?? DEFAULT_RECORDER_MASK_LEVEL,
      maxDuration: readEnv('VITE_UMAMI_RECORDER_MAX_DURATION') ?? DEFAULT_RECORDER_MAX_DURATION,
    });
  }

  initialized = true;
}

export function isUmamiEnabled(): boolean {
  return initialized;
}

export function trackUmamiPageview(to: RouteLocationNormalized): void {
  if (!initialized || !window.umami) {
    return;
  }

  window.umami.track((props) => ({
    ...props,
    url: to.fullPath,
  }));
}
